import { Injectable, computed, signal } from '@angular/core';
import { BillFile, BillStatus, ParsedBillData, CustomerProfile, BillGroup, OnboardingContext } from '../models';
import { GeminiService } from './gemini.service';
import { DriveService } from './drive.service';
import { StorageService } from './storage.service';
import { v4 as uuidv4 } from 'uuid';
import { normalizeAccountNumber, accountNumbersMatch, createBillGroups, determineBillType, isRetriableError, billCommodity } from '../utils/bill-utils';
import { isElectricUsageUnit, isGasUsageUnit, gasUnitLabel } from './bill-persistence.service';
import { GAS_UNIT_LABEL } from '../../zentility/_shared/_model/bills-v4-gas.model';

/** Shown instead of a unit when the portfolio's gas bills are billed in different units. */
const MIXED_GAS_UNITS_LABEL = 'mixed units';
import { CommodityType } from '../../zentility/_shared/_zen-legacy-common/_models/commodity';

/** Undo snapshot: bills plus the pairing rejections active at that moment. */
interface BillHistoryEntry {
  bills: BillFile[];
  rejectedPairings: Set<string>;
}

/**
 * Words the list search matches for a row's commodity, so "gas" or "electric"
 * filters the list without a dedicated column. The row's own commodity wins
 * (a dual-fuel PDF yields one row per commodity); the parsed data's is the fallback.
 */
function commoditySearchText(b: BillFile): string {
  switch (b.commodityType ?? b.data?.commodityType) {
    case CommodityType.Electricity: return 'electricity electric';
    case CommodityType.Gas: return 'natural gas';
    default: return '';
  }
}

@Injectable({
  providedIn: 'root'
})
export class BillService {
  // --- Context ---
  readonly currentContext = signal<OnboardingContext | null>(null);

  // --- State Signals ---
  readonly bills = signal<BillFile[]>([]);
  readonly history = signal<BillHistoryEntry[]>([]); // Undo history stack
  readonly selectedBillIds = signal<string[]>([]);
  readonly selectedEntityIds = signal<string[]>([]);
  readonly selectedAddressIds = signal<string[]>([]);
  readonly selectedMeterIds = signal<string[]>([]);
  readonly resolvingDuplicate = signal<{newBill: BillFile, existingBill: BillFile} | null>(null);
  readonly reviewingPairedGroup = signal<BillGroup | null>(null);
  readonly manualProfile = signal<Partial<CustomerProfile> | null>(null);

  // Detected pairs are presumed real; only explicit rejections are tracked.
  private rejectedPairings = new Set<string>();

  // Computed for undo availability
  readonly canUndo = computed(() => this.history().length > 0);

  // Derived Signals
  readonly customerProfile = computed(() => {
      const derived = this.deriveCustomerProfile(this.bills());
      const manual = this.manualProfile();
      return manual ? { ...derived, ...manual } : derived;
  });

  // Bill AI flow trigger — child components set this to request the bill AI flow opens
  readonly billAiFlowRequested = signal(false);

  // Bill preview — child components set this to request the preview opens
  readonly previewBill = signal<BillFile | null>(null);

  // View State
  readonly isBudgetingMode = signal<boolean>(false);
  readonly searchQuery = signal<string>('');

  // Derived Signals
  readonly processedBills = computed(() => {
      const q = this.searchQuery().toLowerCase();
      if (!q) return this.bills();
      return this.bills().filter(b => {
          return (
            b.file.name.toLowerCase().includes(q) ||
            b.data?.providerName?.toLowerCase().includes(q) ||
            b.data?.legalEntity?.toLowerCase().includes(q) ||
            b.data?.utilityName?.toLowerCase().includes(q) ||
            b.data?.utilityAccountNumber?.toLowerCase().includes(q) ||
            commoditySearchText(b).includes(q)
          );
      });
  });

  readonly stats = computed(() => {
      const all = this.bills();
      // A confirmed pair is two documents for the same energy — count usage
      // once, from the utility half.
      const pairedSupplierIds = new Set(
          this.pairedGroups().map(g => g.supplierBill?.id).filter(Boolean));
      let electric = 0;
      let gas = 0;
      // Gas has no single unit (therms/CCF/MCF/Dth…) — summing across units is
      // meaningless, so track whether all gas bills share one unit for the label.
      let gasUnit: string | null = null;
      let gasUnitsMixed = false;
      all.forEach(b => {
          if (pairedSupplierIds.has(b.id)) return;
          const val = b.data?.usageAmount || 0;
          // The extraction's commodity is authoritative; the unit is only consulted for
          // bills processed before commodityType existed.
          const commodity = b.commodityType ?? b.data?.commodityType;
          const isGasBill = commodity === CommodityType.Gas
              || (commodity !== CommodityType.Electricity
                  && isGasUsageUnit(b.data?.usageUnitId, b.data?.usageUnit));
          if (isGasBill) {
              gas += val;
              // gasUnitLabel() canonicalises known units (ccf → CCF); unknown ones are
              // passed through, so compare those case-insensitively.
              const label = gasUnitLabel(b.data?.usageUnitId, b.data?.usageUnit);
              if (gasUnit == null) gasUnit = label;
              else if (gasUnit.toLowerCase() !== label.toLowerCase()) gasUnitsMixed = true;
          } else if (commodity === CommodityType.Electricity
                     || isElectricUsageUnit(b.data?.usageUnitId, b.data?.usageUnit)) {
              electric += val;
          }
      });
      return {
          electric,
          gas,
          gasUnit: gasUnitsMixed ? MIXED_GAS_UNITS_LABEL : (gasUnit || GAS_UNIT_LABEL.DTH),
          reviewCount: all.filter(b => b.status === BillStatus.REVIEW_NEEDED).length,
          approvedCount: all.filter(b => b.status === BillStatus.APPROVED).length
      };
  });

  readonly errorBills = computed(() => this.bills().filter(b => b.status === BillStatus.ERROR));

  /** Pre-computed retriability per error bill (avoids duplicate computed signals in components). */
  readonly billRetriable = computed(() => {
      const result: Record<string, boolean> = {};
      for (const bill of this.bills()) {
          if (bill.status === BillStatus.ERROR) {
              result[bill.id] = isRetriableError(bill.error);
          }
      }
      return result;
  });

  readonly hierarchy = computed(() => {
      // Depend on pairingVersion so unpairing a bill group updates the hierarchy view.
      this.pairingVersion();
      return this.buildHierarchy(this.processedBills());
  });

  constructor(
      private geminiService: GeminiService,
      private driveService: DriveService,
      private storageService: StorageService
  ) {}

  /**
   * Persist the current state to localStorage.
   * Call after any mutation that should survive a page refresh.
   */
  save(): void {
      const context = this.currentContext();
      if (!context) return;

      const bills = this.bills();
      if (bills.length > 0) {
          this.storageService.saveSession(context, bills, this.manualProfile(), this.isBudgetingMode(), Array.from(this.rejectedPairings));
      } else {
          this.storageService.clearSession(context.storageKey);
      }
  }

  /**
   * Loads saved session from localStorage if available (legacy - portfolio level)
   * Returns true if a session was restored
   * @deprecated Use loadSessionWithContext instead
   */
  async loadSavedSession(): Promise<boolean> {
      const session = await this.storageService.loadSession('portfolio');
      if (session && session.bills.length > 0) {
          return this.loadSessionWithContext(session.context, session.bills, session.isBudgetingMode, session.rejectedPairings);
      }
      return false;
  }

  /**
   * Loads a session with a specific context
   */
  async loadSessionWithContext(context: OnboardingContext, bills: BillFile[], isBudgetingMode: boolean, rejectedPairings: string[] = []): Promise<boolean> {
      // Fix any inconsistent bills (bills with REVIEW_NEEDED/APPROVED status but no data)
      const fixedBills = bills.map(bill => {
          if (!bill.data && (bill.status === BillStatus.REVIEW_NEEDED || bill.status === BillStatus.APPROVED)) {
              console.warn(`[BillService] Fixed inconsistent bill ${bill.id}: status=${bill.status} but data=null. Resetting to PENDING.`);
              return {
                  ...bill,
                  status: BillStatus.PENDING,
                  error: undefined
              };
          }
          return bill;
      });

      this.currentContext.set(context);
      this.bills.set(fixedBills);
      this.manualProfile.set(null);
      this.isBudgetingMode.set(isBudgetingMode);
      this.rejectedPairings = new Set(rejectedPairings);
      this.pairingVersion.update(v => v + 1);

      // Reprocess any bills that were reset to PENDING
      const billsToReprocess = fixedBills.filter(b => b.status === BillStatus.PENDING);
      if (billsToReprocess.length > 0) {
          console.log(`[BillService] Reprocessing ${billsToReprocess.length} bills that need data`);
          billsToReprocess.forEach(b => this.processBill(b.id, b.file));
      }

      return true;
  }

  /**
   * Creates a new session with a specific context
   */
  async createNewSession(context: OnboardingContext): Promise<void> {
      this.currentContext.set(context);
      this.bills.set([]);
      this.manualProfile.set(null);
      this.history.set([]);
      this.selectedBillIds.set([]);
      this.selectedEntityIds.set([]);
      this.selectedAddressIds.set([]);
      this.selectedMeterIds.set([]);
      this.resolvingDuplicate.set(null);
      this.reviewingPairedGroup.set(null);

      // Save empty session to create the storage key
      await this.storageService.saveSession(context, [], null, this.isBudgetingMode());
  }

  /**
   * Clears all bills, resets state, and removes the stored session.
   */
  clearBills(): void {
      const context = this.currentContext();

      // Revoke blob URLs to release PDF binary data from memory
      for (const bill of this.bills()) {
          if (bill.previewUrl) {
              URL.revokeObjectURL(bill.previewUrl);
          }
      }

      // Reset all state signals
      this.bills.set([]);
      this.history.set([]);
      this.selectedBillIds.set([]);
      this.selectedEntityIds.set([]);
      this.selectedAddressIds.set([]);
      this.selectedMeterIds.set([]);
      this.resolvingDuplicate.set(null);
      this.reviewingPairedGroup.set(null);
      this.manualProfile.set(null);
      this.isBudgetingMode.set(false);
      this.searchQuery.set('');
      this.billAiFlowRequested.set(false);
      this.currentContext.set(null);

      // Clear private pairing tracking from previous session
      this.rejectedPairings.clear();

      // Explicitly clear the stored session and bill AI flow state
      if (context) {
          this.storageService.clearSession(context.storageKey);
          this.storageService.clearBillAiFlowState(context.storageKey);
      }
  }

  /**
   * Sets the context without creating/saving an empty session.
   * Use this when the dashboard loads but there's no saved session to restore,
   * so that new file uploads will have the correct context.
   */
  initContext(context: OnboardingContext): void {
      this.currentContext.set(context);
  }

  /**
   * Gets the current context
   */
  getContext(): OnboardingContext | null {
      return this.currentContext();
  }

  /**
   * Checks if there's a saved session available (legacy - portfolio level)
   * @deprecated Use storageService.hasSession(storageKey) instead
   */
  hasSavedSession(): boolean {
      return this.storageService.hasSession('portfolio');
  }

  /**
   * Clears the saved session (legacy - portfolio level)
   * @deprecated Use storageService.clearSession(storageKey) instead
   */
  clearSavedSession(): void {
      this.storageService.clearSession('portfolio');
  }

  // --- Actions ---

  getBills() { return this.bills(); }

  setBills(bills: BillFile[]) {
      this.bills.set(bills);
      this.save();
  }

  addFiles(files: File[]) {
      const newBills: BillFile[] = files.map(file => ({
          id: uuidv4(),
          file,
          previewUrl: URL.createObjectURL(file),
          status: BillStatus.PENDING,
          data: null
      }));

      this.bills.update(current => [...current, ...newBills]);
      this.save();

      // Trigger processing
      newBills.forEach(b => this.processBill(b.id, b.file));
  }

  async processBill(id: string, file: File) {
      this.updateBillStatus(id, BillStatus.PROCESSING);
      // Reprocessing a dual-fuel PDF recreates its split-out sibling rows
      this.bills.update(current => current.filter(b => b.dualFuelOf !== id));
      try {
          // Pass context for tiered extraction (performance optimization)
          const context = this.currentContext();
          const result = await this.geminiService.processBill(file, this.isBudgetingMode(), context || undefined);
          const [primary, ...siblings] = result.extractions;
          this.updateBill(id, {
              data: primary.data,
              status: BillStatus.REVIEW_NEEDED,
              commodityType: primary.commodityType,
              commodityTypes: result.commodityTypes,
              warnings: result.warnings,
              rawExtraction: primary.rawExtraction,
              tempFileId: result.tempFileId,
              billAiId: primary.billAiId,
              periodIndex: primary.periodIndex,
              periodCount: primary.periodCount
          });

          // Dual-fuel and split billing periods: every additional extraction becomes its
          // own bill row sharing the same PDF (a gas row groups into its own meter).
          const primaryBill = this.bills().find(b => b.id === id);
          if (siblings.length && primaryBill) {
              const siblingBills: BillFile[] = siblings.map(ext => ({
                  id: uuidv4(),
                  file,
                  previewUrl: primaryBill.previewUrl,
                  status: BillStatus.REVIEW_NEEDED,
                  data: ext.data,
                  commodityType: ext.commodityType,
                  commodityTypes: result.commodityTypes,
                  warnings: result.warnings,
                  rawExtraction: ext.rawExtraction,
                  tempFileId: result.tempFileId,
                  billAiId: ext.billAiId,
                  periodIndex: ext.periodIndex,
                  periodCount: ext.periodCount,
                  dualFuelOf: id
              }));
              this.bills.update(current => [...current, ...siblingBills]);
          }

          // Check for duplicates immediately after processing
          this.bills()
              .filter(b => b.id === id || b.dualFuelOf === id)
              .forEach(b => this.checkDuplicates(b));

          // Pairing is shown inline in the bill list (pair spine); no popup.
          this.save();
      } catch (error) {
          console.error(`Error processing bill ${id}`, error);
          const errorMsg = error instanceof Error ? error.message : 'AI Processing Failed';
          this.updateBill(id, { status: BillStatus.ERROR, error: errorMsg });
          this.save();
      }
  }

  updateBill(id: string, updates: Partial<BillFile>) {
      this.bills.update(current =>
          current.map(b => b.id === id ? { ...b, ...updates } : b)
      );

  }

  updateBillData(id: string, data: ParsedBillData) {
      this.updateBill(id, { data });
      const updatedBill = this.bills().find(b => b.id === id);
      if (updatedBill) {
          this.checkDuplicates(updatedBill);
      }
      this.save();
  }

  checkDuplicates(bill: BillFile) {
      if (!bill.data) return;

      // Determine the bill type (utility vs supplier)
      const billType = determineBillType(bill.data);

      const existing = this.bills().find(b => {
          if (b.id === bill.id) return false; // Skip self

          // Rows split from one upload (dual-fuel commodities, billing periods) are companions, not duplicates
          if (b.dualFuelOf === bill.id || bill.dualFuelOf === b.id || (bill.dualFuelOf && bill.dualFuelOf === b.dualFuelOf)) return false;

          // Bills for different commodities never duplicate each other.
          if (billCommodity(bill) !== billCommodity(b)) return false;

          // 1. File Name Match (Strong signal, but simple)
          if (b.file.name === bill.file.name) return true;

          if (!b.data) return false;

          // IMPORTANT: Only consider bills of the SAME TYPE as potential duplicates
          // Utility + Supplier bills with same period/UAN are PAIRED, not duplicates!
          const existingBillType = determineBillType(b.data);
          if (billType !== existingBillType) return false;

          // 2. Semantic Match (Start Date + End Date + UAN + SAME BILL TYPE)
          // All must match to consider it a duplicate based on data
          const sameStartDate = b.data.billingPeriodStart === bill.data?.billingPeriodStart;
          const sameEndDate = b.data.billingPeriodEnd === bill.data?.billingPeriodEnd;

          // Compare UANs using normalized comparison (removes whitespace, dashes)
          // Check all three UAN fields for any match
          const billUANs = [
              normalizeAccountNumber(bill.data?.utilityAccountNumber),
              normalizeAccountNumber(bill.data?.utilityAccountNumber2),
              normalizeAccountNumber(bill.data?.utilityAccountNumber3)
          ].filter(u => u.length > 0);

          const existingUANs = [
              normalizeAccountNumber(b.data.utilityAccountNumber),
              normalizeAccountNumber(b.data.utilityAccountNumber2),
              normalizeAccountNumber(b.data.utilityAccountNumber3)
          ].filter(u => u.length > 0);

          // Check if any UAN matches between the two bills
          const sameUAN = billUANs.length > 0 &&
              existingUANs.length > 0 &&
              billUANs.some(newUan => existingUANs.some(existingUan => newUan === existingUan));

          return sameStartDate && sameEndDate && sameUAN;
      });

      if (existing) {
          this.updateBill(bill.id, {
              status: BillStatus.DUPLICATE_DETECTED,
              duplicateOfId: existing.id
          });
          this.resolvingDuplicate.set({ newBill: bill, existingBill: existing });
      }
  }

  resolveDuplicate(action: 'keep' | 'delete') {
      const current = this.resolvingDuplicate();
      if (!current) return;

      if (action === 'delete') {
          this.deleteBill(current.newBill.id);
      } else {
          this.updateBill(current.newBill.id, {
              status: BillStatus.REVIEW_NEEDED,
              duplicateOfId: undefined
          });
      }
      this.resolvingDuplicate.set(null);
      this.save();

      // Check if there are more duplicates to review
      this.showNextDuplicate();
  }

  /**
   * Opens the duplicate resolver for a specific bill that has DUPLICATE_DETECTED status.
   * This allows users to click on duplicate badges to review them.
   */
  openDuplicateResolver(bill: BillFile) {
      if (bill.status !== BillStatus.DUPLICATE_DETECTED || !bill.duplicateOfId) return;

      const existingBill = this.bills().find(b => b.id === bill.duplicateOfId);
      if (existingBill) {
          this.resolvingDuplicate.set({ newBill: bill, existingBill });
      }
  }

  /**
   * Checks for any remaining bills with DUPLICATE_DETECTED status and shows the next one.
   */
  private showNextDuplicate() {
      const nextDuplicate = this.bills().find(b =>
          b.status === BillStatus.DUPLICATE_DETECTED && b.duplicateOfId
      );

      if (nextDuplicate) {
          const existingBill = this.bills().find(b => b.id === nextDuplicate.duplicateOfId);
          if (existingBill) {
              this.resolvingDuplicate.set({ newBill: nextDuplicate, existingBill });
          }
      }
  }

  // --- Paired Bill Detection & Resolution ---

  /** Bumped whenever a pairing decision changes so computeds can react. */
  private pairingVersion = signal(0);

  /** All bill groups with pairing rejections applied. */
  private readonly billGroups = computed(() => {
      this.pairingVersion();
      return createBillGroups(this.bills(), this.isPairingRejectedFn);
  });

  /** Detected utility+supplier pairs, excluding rejected ones. */
  readonly pairedGroups = computed(() =>
      this.billGroups().filter(g =>
          g.utilityBill && g.supplierBill &&
          g.matchConfidence !== 'unpaired'));

  /** Pairs the user explicitly unpaired — shown with a re-pair affordance. */
  readonly rejectedGroups = computed(() => this.billGroups().filter(g => g.rejected));

  /**
   * Opens the paired bill resolver for a specific group.
   * Can be called when clicking on a paired row in the UI.
   */
  openPairedBillResolver(group: BillGroup) {
      this.reviewingPairedGroup.set(group);
  }

  /**
   * Resolves a paired bill review.
   * @param action 'confirm' to keep the pairing, 'unpair' to reject it,
   *   'pair' to restore a rejected pairing, 'close' to dismiss
   */
  resolvePairedBill(action: 'confirm' | 'unpair' | 'pair' | 'close') {
      const current = this.reviewingPairedGroup();
      if (!current) return;

      if (current.utilityBill && current.supplierBill) {
          const pairingKey = this.getPairingKey(current.utilityBill.id, current.supplierBill.id);

          // Pairing is presumed (pairedGroups excludes only rejections) — 'confirm'
          // just leaves the pair in place. Unpair/pair are undoable.
          if (action === 'unpair' || action === 'pair') {
              this.addToHistory();
              if (action === 'unpair') {
                  this.rejectedPairings.add(pairingKey);
              } else {
                  this.rejectedPairings.delete(pairingKey);
              }
              this.save();
          }
          this.pairingVersion.update(v => v + 1);
      }

      this.reviewingPairedGroup.set(null);
  }

  /**
   * Generates a consistent key for a pairing regardless of bill order.
   */
  private getPairingKey(id1: string, id2: string): string {
      return [id1, id2].sort().join('|');
  }

  /**
   * Checks if a pairing has been rejected.
   */
  isPairingRejected(utilityBillId: string, supplierBillId: string): boolean {
      return this.rejectedPairings.has(this.getPairingKey(utilityBillId, supplierBillId));
  }

  /** Bound predicate for createBillGroups so pairing skips rejected pairs. */
  private isPairingRejectedFn = (utilityBillId: string, supplierBillId: string): boolean =>
      this.isPairingRejected(utilityBillId, supplierBillId);

  updateBillStatus(id: string, status: BillStatus) {
      this.updateBill(id, { status });
  }

  deleteBill(id: string) {
      this.deleteBills([id]);
  }

  /**
   * Deleting a dual-fuel PDF's primary row takes its split-out sibling rows with it — they
   * share the document, so the user is removing the PDF, not one commodity of it. Deleting a
   * sibling on its own leaves the primary in place.
   */
  deleteBills(ids: string[]) {
      if (ids.length === 0) return;
      this.addToHistory();
      const idSet = new Set(ids);
      this.bills.update(current => current.filter(b => !idSet.has(b.id) && !(b.dualFuelOf && idSet.has(b.dualFuelOf))));
      const remaining = new Set(this.bills().map(b => b.id));
      this.selectedBillIds.update(sel => sel.filter(i => remaining.has(i)));
      this.save();
  }

  // --- Wizard ↔ Dashboard Sync ---

  /**
   * Syncs bill data edited in the wizard back to BillService.
   * Uses updateBill() directly to avoid triggering duplicate checks.
   */
  syncBillDataFromWizard(billFileId: string, data: ParsedBillData): void {
      this.updateBill(billFileId, { data: { ...data } });
      this.save();
  }

  /**
   * Renames legalEntity on all bills whose legalEntity matches any source name.
   * Called by the wizard store after a LEN merge to keep the hierarchy in sync.
   */
  mergeLenNamesInBills(sourceNames: string[], targetName: string): void {
      if (sourceNames.length === 0) return;
      const sourceSet = new Set(sourceNames.map(s => s.trim().toLowerCase()));
      this.bills.update(bills =>
          bills.map(b => {
              if (b.data && sourceSet.has((b.data.legalEntity || '').trim().toLowerCase())) {
                  return { ...b, data: { ...b.data, legalEntity: targetName } };
              }
              return b;
          })
      );
      this.save();
  }

  // --- Bill AI Flow ---

  requestBillAiFlow() {
      this.billAiFlowRequested.set(true);
  }

  clearBillAiFlowRequest() {
      this.billAiFlowRequested.set(false);
  }

  toggleProMode() {
      this.isBudgetingMode.update(v => !v);
      this.save();
  }

  setSearchQuery(query: string) {
      this.searchQuery.set(query);
  }

  updateProfile(profile: CustomerProfile) {
      this.manualProfile.set(profile);
      this.save();
  }

  // --- Selection ---

  toggleSelection(id: string, type: 'bill' | 'entity' | 'address' | 'meter') {
      const signalMap = {
        bill: this.selectedBillIds,
        entity: this.selectedEntityIds,
        address: this.selectedAddressIds,
        meter: this.selectedMeterIds
      };
      signalMap[type].update(ids => ids.includes(id) ? ids.filter(i => i !== id) : [...ids, id]);
  }

  clearSelection() {
      this.selectedBillIds.set([]);
      this.selectedEntityIds.set([]);
      this.selectedAddressIds.set([]);
      this.selectedMeterIds.set([]);
  }

  selectAll(filteredBillIds: string[]) {
      if (this.selectedBillIds().length === filteredBillIds.length && filteredBillIds.length > 0) {
          this.selectedBillIds.set([]);
      } else {
          this.selectedBillIds.set(filteredBillIds);
      }
  }

  // --- Merge Operations ---

  /**
   * Merge multiple entities into a single target entity.
   * All bills with the source entity names will be updated to use the target entity name.
   */
  mergeEntities(selectedEntityIds: string[], targetEntityId: string) {
      if (selectedEntityIds.length < 2) return;

      this.addToHistory();

      this.bills.update(current => current.map(b => {
          if (b.data && selectedEntityIds.includes((b.data.legalEntity || '').trim())) {
              return {
                  ...b,
                  data: {
                      ...b.data,
                      legalEntity: targetEntityId
                  }
              };
          }
          return b;
      }));

      this.selectedEntityIds.set([]);
      this.save();
  }

  /**
   * Delete entities and all bills associated with them.
   * All bills with the selected entity names will be removed.
   */
  deleteEntities(selectedEntityIds: string[]) {
      if (selectedEntityIds.length === 0) return;

      this.addToHistory();

      // Build a temporary hierarchy to find all bills in the selected entities
      // Use this.bills() instead of processedBills() to include all bills regardless of filters
      const billIdsToDelete = new Set<string>();
      const hierarchy = this.buildHierarchy(this.bills()) as any[];

      hierarchy.forEach((entityGroup: any) => {
          // entityGroup has properties: id, name (both are the entity name)
          if (selectedEntityIds.includes(entityGroup.name) || selectedEntityIds.includes(entityGroup.id)) {
              Object.values(entityGroup.addresses).forEach((address: any) => {
                  Object.values(address.meters).forEach((meter: any) => {
                      meter.bills.forEach((bill: BillFile) => {
                          billIdsToDelete.add(bill.id);
                      });
                  });
              });
          }
      });

      this.bills.update(current => current.filter(b => !billIdsToDelete.has(b.id)));
      this.selectedEntityIds.set([]);
      this.save();
  }

  /**
   * Delete addresses and all bills associated with them.
   * All bills with the selected addresses will be removed.
   */
  deleteAddresses(selectedAddressIds: string[]) {
      if (selectedAddressIds.length === 0) return;

      this.addToHistory();

      // We need to figure out which bills belong to which address by looking at the hierarchy
      // Since addresses can have multiple bills grouped together, we can't just check individual bills
      // Instead, we should get all bill IDs that are part of the selected addresses from the hierarchy
      const billIdsToDelete = new Set<string>();

      // Build a temporary hierarchy to find all bills in the selected addresses
      // Use this.bills() instead of processedBills() to include all bills regardless of filters
      const hierarchy = this.buildHierarchy(this.bills()) as any[];
      hierarchy.forEach((entityGroup: any) => {
          Object.values(entityGroup.addresses).forEach((address: any) => {
              if (selectedAddressIds.includes(address.id)) {
                  Object.values(address.meters).forEach((meter: any) => {
                      meter.bills.forEach((bill: BillFile) => {
                          billIdsToDelete.add(bill.id);
                      });
                  });
              }
          });
      });

      this.bills.update(current => current.filter(b => !billIdsToDelete.has(b.id)));
      this.selectedAddressIds.set([]);
      this.save();
  }

  /**
   * Delete meters and all bills associated with them.
   * All bills within the selected meters will be removed.
   */
  countBillsForMeters(selectedMeterIds: string[]): number {
      let count = 0;
      const hierarchy = this.buildHierarchy(this.bills()) as any[];
      hierarchy.forEach((entityGroup: any) => {
          Object.values(entityGroup.addresses).forEach((address: any) => {
              Object.values(address.meters).forEach((meter: any) => {
                  if (selectedMeterIds.includes(meter.id)) {
                      count += (meter.bills?.length || 0);
                  }
              });
          });
      });
      return count;
  }

  deleteMeters(selectedMeterIds: string[]) {
      if (selectedMeterIds.length === 0) return;

      this.addToHistory();

      const billIdsToDelete = new Set<string>();
      const hierarchy = this.buildHierarchy(this.bills()) as any[];

      hierarchy.forEach((entityGroup: any) => {
          Object.values(entityGroup.addresses).forEach((address: any) => {
              Object.values(address.meters).forEach((meter: any) => {
                  if (selectedMeterIds.includes(meter.id)) {
                      meter.bills.forEach((bill: BillFile) => {
                          billIdsToDelete.add(bill.id);
                      });
                  }
              });
          });
      });

      this.bills.update(current => current.filter(b => !billIdsToDelete.has(b.id)));
      this.selectedMeterIds.set([]);
      this.save();
  }

  /**
   * Merge multiple addresses into a single target address.
   * All bills with the source addresses will be updated to use the target address.
   */
  mergeAddresses(selectedAddressIds: string[], targetAddressId: string) {
      if (selectedAddressIds.length < 2) return;

      const [targetEntity, targetStreet, targetCity] = targetAddressId.split('::');

      // Find a representative bill to get state/zip
      const targetBill = this.bills().find(b =>
          b.data &&
          (b.data.legalEntity || '').trim() === targetEntity &&
          (b.data.serviceAddressStreet1 || '').trim() === targetStreet &&
          (b.data.serviceAddressCity || '').trim() === targetCity
      );

      const fullTargetDetails = targetBill?.data || {
          serviceAddressState: '',
          serviceAddressZip: ''
      };

      this.addToHistory();

      this.bills.update(current => current.map(b => {
          const currentId = this.getAddressId(
              (b.data?.legalEntity || '').trim(),
              (b.data?.serviceAddressStreet1 || '').trim(),
              (b.data?.serviceAddressCity || '').trim()
          );

          if (selectedAddressIds.includes(currentId) && b.data) {
              return {
                  ...b,
                  data: {
                      ...b.data,
                      legalEntity: targetEntity,
                      serviceAddressStreet1: targetStreet,
                      serviceAddressCity: targetCity,
                      serviceAddressState: fullTargetDetails.serviceAddressState || b.data.serviceAddressState,
                      serviceAddressZip: fullTargetDetails.serviceAddressZip || b.data.serviceAddressZip
                  }
              };
          }
          return b;
      }));

      this.selectedAddressIds.set([]);
      this.save();
  }

  /**
   * Helper to get address ID in format "Entity::Street::City"
   */
  getAddressId(entityName: string, street: string, city: string): string {
      return `${entityName}::${street}::${city}`;
  }

  /**
   * Get bills that match the selected bill IDs (for comparison)
   */
  getSelectedBills(): BillFile[] {
      const selectedIds = this.selectedBillIds();
      return this.bills().filter(b => selectedIds.includes(b.id));
  }

  // --- History / Undo ---

  private addToHistory() {
      const entry: BillHistoryEntry = {
          bills: this.bills(),
          rejectedPairings: new Set(this.rejectedPairings)
      };
      // Keep last 10 states
      this.history.update(prev => [...prev.slice(-9), entry]);
  }

  undo() {
      const historyStack = this.history();
      if (historyStack.length === 0) return;

      const previousState = historyStack[historyStack.length - 1];
      this.bills.set(previousState.bills);
      this.rejectedPairings = new Set(previousState.rejectedPairings);
      this.pairingVersion.update(v => v + 1);
      this.history.update(prev => prev.slice(0, -1));
      this.clearSelection();
      this.save();
  }

  // --- Move / Drag-Drop Operations ---

  /**
   * Move a bill to a different address (Entity::Street::City)
   */
  moveBillToAddress(billId: string, targetAddressId: string) {
      const [targetEntity, targetStreet, targetCity] = targetAddressId.split('::');

      const bill = this.bills().find(b => b.id === billId);
      if (!bill || !bill.data) return;

      // Check if already at this location
      const currentEntity = (bill.data.legalEntity || '').trim();
      const currentStreet = (bill.data.serviceAddressStreet1 || '').trim();
      const currentCity = (bill.data.serviceAddressCity || '').trim();

      if (currentEntity === targetEntity && currentStreet === targetStreet && currentCity === targetCity) {
          return; // Already at target
      }

      this.addToHistory();

      this.bills.update(current => current.map(b => {
          if (b.id === billId && b.data) {
              return {
                  ...b,
                  data: {
                      ...b.data,
                      legalEntity: targetEntity,
                      serviceAddressStreet1: targetStreet,
                      serviceAddressCity: targetCity
                  }
              };
          }
          return b;
      }));
      this.save();
  }

  /**
   * Move a bill to a meter (updates the bill's account number to match the meter group)
   */
  moveBillToMeter(billId: string, targetMeterId: string, targetAccountNumber: string) {
      const bill = this.bills().find(b => b.id === billId);
      if (!bill || !bill.data) return;

      this.addToHistory();

      // Update the bill's account number to match the target meter
      this.bills.update(current => current.map(b => {
          if (b.id === billId && b.data) {
              return {
                  ...b,
                  data: {
                      ...b.data,
                      utilityAccountNumber: targetAccountNumber
                  }
              };
          }
          return b;
      }));
      this.save();
  }

  /**
   * Move an address (all its bills) to a different entity
   */
  moveAddressToEntity(addressId: string, targetEntityName: string) {
      const [sourceEntity, sourceStreet, sourceCity] = addressId.split('::');

      if (sourceEntity === targetEntityName) return; // Already at target

      this.addToHistory();

      this.bills.update(current => current.map(b => {
          if (b.data &&
              (b.data.legalEntity || '').trim() === sourceEntity &&
              (b.data.serviceAddressStreet1 || '').trim() === sourceStreet &&
              (b.data.serviceAddressCity || '').trim() === sourceCity) {
              return {
                  ...b,
                  data: {
                      ...b.data,
                      legalEntity: targetEntityName
                  }
              };
          }
          return b;
      }));

      this.selectedAddressIds.set([]);
      this.save();
  }

  /**
   * Move a meter (all its bills) to a different address
   */
  moveMeterToAddress(meterId: string, billIds: string[], targetAddressId: string) {
      const [targetEntity, targetStreet, targetCity] = targetAddressId.split('::');

      this.addToHistory();

      this.bills.update(current => current.map(b => {
          if (billIds.includes(b.id) && b.data) {
              return {
                  ...b,
                  data: {
                      ...b.data,
                      legalEntity: targetEntity,
                      serviceAddressStreet1: targetStreet,
                      serviceAddressCity: targetCity
                  }
              };
          }
          return b;
      }));
      this.save();
  }

  private deriveCustomerProfile(bills: BillFile[]): CustomerProfile {
      if (bills.length === 0) {
          return {
              companyName: '',
              billingAddressStreet1: '',
              billingAddressStreet2: '',
              billingAddressCity: '',
              billingAddressState: '',
              billingAddressZip: '',
              contactFirstName: '',
              contactLastName: '',
              contactEmail: '',
              contactPhone: '',
              isApproved: false
          };
      }

      // Find most frequent entity
      const entityCounts: Record<string, number> = {};
      bills.forEach(b => {
          const entity = b.data?.legalEntity?.trim();
          if (entity) {
              entityCounts[entity] = (entityCounts[entity] || 0) + 1;
          }
      });

      const topEntity = Object.keys(entityCounts).sort((a, b) => entityCounts[b] - entityCounts[a])[0] || '';

      // Find representative bill for this entity to extract other details
      const repBill = bills.find(b => b.data?.legalEntity?.trim() === topEntity) || bills[0];
      const data = repBill.data;

      // In a real app, we might extract contact info from specific fields or an inferred profile.
      // Here we default to what's available or empty.

      return {
          companyName: topEntity,
          billingAddressStreet1: data?.billingAddressStreet1 || data?.serviceAddressStreet1 || '',
          billingAddressStreet2: data?.billingAddressStreet2 || data?.serviceAddressStreet2 || '',
          billingAddressCity: data?.billingAddressCity || data?.serviceAddressCity || '',
          billingAddressState: data?.billingAddressState || data?.serviceAddressState || '',
          billingAddressZip: data?.billingAddressZip || data?.serviceAddressZip || '',
          contactFirstName: '', // Not typically on bill
          contactLastName: '',
          contactEmail: '',
          contactPhone: '',
          isApproved: false
      };
  }

  // --- Hierarchy Logic ---

  /**
   * Normalizes a string for comparison in hierarchy building.
   * Uses account number normalization rules: removes whitespace and dashes,
   * preserves alphanumeric characters and leading zeros.
   */
  private normalizeString(str: string | undefined | null) {
      if (!str) return '';
      // Use consistent normalization - remove whitespace/dashes, keep alphanumeric
      // Uppercase for case-insensitive comparison in hierarchy grouping
      return normalizeAccountNumber(str).toUpperCase();
  }

  private buildHierarchy(bills: BillFile[]) {
      // Separate processing and processed bills for better organization
      // Bills without data should always be treated as processing, regardless of status
      // Error bills are excluded from the hierarchy — they have no data to group by
      const processingBills = bills.filter(b =>
          b.status !== BillStatus.ERROR && (
          !b.data ||
          b.status === BillStatus.PENDING ||
          b.status === BillStatus.PROCESSING
          )
      );
      const processedBills = bills.filter(b =>
          b.data &&
          b.status !== BillStatus.PENDING &&
          b.status !== BillStatus.PROCESSING &&
          b.status !== BillStatus.ERROR
      );

      // 1. Pre-process Grouping by ID Connectivity (only for processed bills)
      const billIdentifiers = new Map<string, Set<string>>();

      processedBills.forEach(bill => {
          const ids = new Set<string>();
          if (bill.data?.utilityAccountNumber) ids.add(this.normalizeString(bill.data.utilityAccountNumber));
          if (bill.data?.additionalIdentifiers) {
              bill.data.additionalIdentifiers.forEach((identifier) => ids.add(this.normalizeString(identifier.value)));
          }
          const validIds = new Set<string>();
          ids.forEach(id => { if(id.length > 2) validIds.add(id); });
          billIdentifiers.set(bill.id, validIds);
      });

      const parent = new Map<string, string>();
      const find = (id: string): string => {
          if (!parent.has(id)) parent.set(id, id);
          if (parent.get(id) !== id) parent.set(id, find(parent.get(id)!));
          return parent.get(id)!;
      };
      const union = (id1: string, id2: string) => {
          const root1 = find(id1);
          const root2 = find(id2);
          if (root1 !== root2) parent.set(root1, root2);
      };

      const identifierToBills = new Map<string, string[]>();
      billIdentifiers.forEach((ids, billId) => {
          find(billId);
          ids.forEach(id => {
              if (!identifierToBills.has(id)) identifierToBills.set(id, []);
              identifierToBills.get(id)!.push(billId);
          });
      });

      identifierToBills.forEach((billList) => {
          for (let i = 1; i < billList.length; i++) {
              union(billList[0], billList[i]);
          }
      });

      const componentGroups = new Map<string, BillFile[]>();
      processedBills.forEach(bill => {
          const root = find(bill.id);
          if (!componentGroups.has(root)) componentGroups.set(root, []);
          componentGroups.get(root)!.push(bill);
      });

      // Add processing bills to a special group if any exist
      if (processingBills.length > 0) {
          componentGroups.set('__processing__', processingBills);
      }

      const groups: Record<string, any> = {};

      componentGroups.forEach((groupBills) => {
          const entities: Record<string, number> = {};
          const addresses: Record<string, number> = {};
          let totalElectric = 0;
          let totalGas = 0;

          groupBills.forEach(b => {
              const e = (b.data?.legalEntity || 'Unknown Entity').trim();
              const s = (b.data?.serviceAddressStreet1 || 'Unknown Address').trim();
              const c = (b.data?.serviceAddressCity || '').trim();
              const addrKey = `${s}::${c}`;

              entities[e] = (entities[e] || 0) + 1;
              addresses[addrKey] = (addresses[addrKey] || 0) + 1;

              const val = b.data?.usageAmount || 0;
              // The extraction's commodity is authoritative; the unit is only consulted
              // for bills processed before commodityType existed.
              const commodity = b.commodityType ?? b.data?.commodityType;
              if (commodity === CommodityType.Gas) totalGas += val;
              else if (commodity === CommodityType.Electricity) totalElectric += val;
              else if (isElectricUsageUnit(b.data?.usageUnitId, b.data?.usageUnit)) totalElectric += val;
              else if (isGasUsageUnit(b.data?.usageUnitId, b.data?.usageUnit)) totalGas += val;
          });

          const entityName = Object.keys(entities).sort((a,b) => entities[b] - entities[a])[0];
          const addressKey = Object.keys(addresses).sort((a,b) => addresses[b] - addresses[a])[0];
          const [street, city] = addressKey.split('::');

          const repBill = groupBills.find(b => (b.data?.serviceAddressStreet1 || '').trim() === street) || groupBills[0];
          const state = repBill.data?.serviceAddressState || '';

          const addressId = `${entityName}::${street}::${city}`;
          const meterId = `meter_group_${find(groupBills[0].id)}`;

          // Check if any bills in this group are still processing
          const groupProcessingBills = groupBills.filter(b =>
              b.status === BillStatus.PENDING || b.status === BillStatus.PROCESSING
          );
          const isLoading = groupProcessingBills.length > 0;
          const loadingBillCount = groupProcessingBills.length;

          if (!groups[entityName]) {
              groups[entityName] = {
                  id: entityName,
                  name: entityName,
                  metrics: { electric: 0, gas: 0 },
                  billCount: 0,
                  isLoading: false,
                  loadingBillCount: 0,
                  addresses: {}
              };
          }
          const grp = groups[entityName];
          grp.billCount += groupBills.length;
          grp.metrics.electric += totalElectric;
          grp.metrics.gas += totalGas;
          // Update loading state - true if any sub-group is loading
          if (isLoading) {
              grp.isLoading = true;
              grp.loadingBillCount += loadingBillCount;
          }

          if (!grp.addresses[addressId]) {
              grp.addresses[addressId] = {
                  id: addressId,
                  name: street,
                  city: city,
                  state: state,
                  metrics: { electric: 0, gas: 0 },
                  isLoading: false,
                  loadingBillCount: 0,
                  meters: {}
              };
          }
          const addr = grp.addresses[addressId];
          addr.metrics.electric += totalElectric;
          addr.metrics.gas += totalGas;
          // Update loading state for address
          if (isLoading) {
              addr.isLoading = true;
              addr.loadingBillCount += loadingBillCount;
          }

          // Create bill groups for this meter (pairs utility + supplier bills)
          const billGroups = createBillGroups(groupBills, this.isPairingRejectedFn);

          addr.meters[meterId] = {
              id: meterId,
              accountNumber: repBill.data?.utilityAccountNumber || 'Unknown',
              metrics: { electric: totalElectric, gas: totalGas },
              bills: groupBills,
              billGroups: billGroups
          };
      });

      return Object.values(groups).sort((a: any, b: any) => b.metrics.electric - a.metrics.electric);
  }
}
